import React, { useEffect, useState } from 'react'
import { FaEdit, FaTrash, FaPlus, FaTimes, FaImage, FaEye } from 'react-icons/fa'
import { baseUrl } from '../../Environments/environment'
import { apiClient } from '../../api'
import { Notification, ConfirmationModal, useNotification, useConfirmation } from '../NotificationComponent/Notifications'

const RoomType = ({ isOpen, onClose, roomData, action, onDataChange }) => {
  const [formData, setFormData] = useState({
    tierType: roomData?.tierType || '',
    tierTypeId: roomData?.tierTypeId || '',
    roomType: roomData?.roomType || '',
    price: roomData?.price || '',
    roomCode: roomData?.roomCode || '',
    description: roomData?.description || '',
    images: roomData?.images || []
  })

  const [isCreateMode, setIsCreateMode] = useState(action === 'create')
  const [isUpdateMode, setIsUpdateMode] = useState(action === 'update')
  const [tierType, setTierType] = useState(undefined)
  const [selectedTierType, setSelectedTierType] = useState(undefined)
  const [isEditing, setIsEditing] = useState(action === 'create') // Start in edit mode for create, view mode for update
  const [isLoading, setIsLoading] = useState(false)
  
  // Custom hooks for notifications and confirmations
  const { showNotification, notificationMessage, notificationType, showNotificationMessage, hideNotification } = useNotification()
  const { showConfirm, confirmTitle, confirmMessage, isLoading: confirmLoading, showConfirmation, handleConfirm, handleCancel } = useConfirmation()

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const validateForm = () => {
    if (!formData.tierTypeId || !formData.roomType || !formData.price) {
      showNotificationMessage("Please fill in all required fields (Tier Type, Room Type, and Price)", 'error');
      return false;
    }
    return true;
  }

  const handleImageAdd = (event) => {
    const files = Array.from(event.target.files)
    const newImages = files.map(file => ({
      id: Date.now() + Math.random(),
      file,
      preview: URL.createObjectURL(file)
    }))
    
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...newImages]
    }))
  }

  const handleImageRemove = (imageId) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter(img => img.id !== imageId)
    }))
  }

  const handleUpdate = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    try {
      const propertyId = localStorage.getItem("selectedPropertyId");
      const token = localStorage.getItem("accessToken");
      const data = new FormData();
      data.append("roomType", formData.roomType);
      data.append("price", formData.price);
      data.append("description", formData.description || "");
      data.append("tierType", formData.tierType || "");
      data.append("tierTypeId", formData.tierTypeId || "");
      data.append("roomCode", formData.roomCode || "");

      if (isCreateMode) {
        formData.images.forEach(img => {
          data.append("images", img.file); // only the File object
        });
        console.log("formData :", formData);
        // Create new room types
        const response = await apiClient.post(
          `${baseUrl}/upgradeRoomWorkflow/roomType/${propertyId}`,
          data,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
        if (response.status === 200 || response.status === 201) {
          console.log("Room type created successfully:", response.data);
          showNotificationMessage("Room type created successfully!", 'success');
          onDataChange && onDataChange(); // Notify parent to refresh data
          setTimeout(() => onClose(), 1500);
        }
      } else if (isUpdateMode) {
        // Update existing room type
        formData.id = roomData.id;
        formData.images.forEach(img => {
          if (img.file) {
            // new file → send via FormData as file
            data.append("images", img.file);
          } else if (typeof img === "string" || img.preview?.startsWith("http")) {
            // existing image (URL) → send as text
            data.append("existingImages", img.preview || img);
          }
        });
        data.append("id", roomData.id);
        console.log("formData :", formData);
        console.log("roomData :", roomData);
        const response = await apiClient.put(
          `${baseUrl}/upgradeRoomWorkflow/roomType/${propertyId}`,
          data,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
        if (response.status === 200) {
          console.log("Room type updated successfully:", response.data);
          showNotificationMessage("Room type updated successfully!", 'success');
          onDataChange && onDataChange(); // Notify parent to refresh data
          setTimeout(() => onClose(), 1500);
        }
      }
    } catch (error) {
      console.error("Error saving room type:", error.response.data.message);
      showNotificationMessage(error.response.data.message, 'error');
    } finally {
      setIsLoading(false);
    }
  }

  const handleDeleteClick = () => {
    const deleteAction = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const data = {
          id: roomData.id
        }
        console.log("data :", data);
        const propertyId = localStorage.getItem("selectedPropertyId");
        const response = await apiClient.delete(
          `${baseUrl}/upgradeRoomWorkflow/roomType/${propertyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            data,
          }
        );
        
        if (response.status === 200) {
          console.log("Room type deleted successfully");
          showNotificationMessage("Room type deleted successfully!", 'success');
          onDataChange && onDataChange(); // Notify parent to refresh data
          setTimeout(() => onClose(), 1500);
        }
      } catch (error) {
        console.error("Error deleting room type:", error.response.data.message);
        showNotificationMessage(error.response.data.message, 'error');
      }
    }

    showConfirmation(
      "Confirm Deletion",
      `Are you sure you want to delete <span class="font-semibold text-gray-900">"${formData.roomType}"</span>? This action cannot be undone.`,
      deleteAction
    );
  }

  // Fetch tier data for dropdown when editing
  const getTierType = async () => {
    try{
        const propertyId = localStorage.getItem("selectedPropertyId")
        const token = localStorage.getItem("accessToken")
        const response = await apiClient.get(`${baseUrl}/upgradeRoomWorkflow/tierData/${propertyId}`,{
            headers: {
                Authorization: `Bearer ${token}`
            }
        })
        if(response.status === 200){
            console.log("Tier Data Fetched :", response.data.result.tierData)
            setTierType(response.data.result.tierData)
        }
    }catch(error){
        console.error("Error fetching data:", error)
    }
  }

  useEffect(() => {
    getTierType()
  }, [isOpen])

  // Update formData when roomData changes (for initial display from parent)
  useEffect(() => {
    if (roomData) {
      setFormData({
        tierType: roomData.tierType || '',
        tierTypeId: roomData.tierTypeId || '',
        roomType: roomData.roomType || '',
        price: roomData.price || '',
        roomCode: roomData.roomCode || '',
        description: roomData.description || '',
        images: roomData.images || []
      })
      // Set selected tier for dropdown
      if (roomData.tierTypeId) {
        setSelectedTierType(roomData.tierTypeId)
      }
    }
  }, [roomData])

  // Update states when action changes
  useEffect(() => {
    setIsCreateMode(action === 'create')
    setIsUpdateMode(action === 'update')
    setIsEditing(action === 'create') // Start in edit mode for create, view mode for update
  }, [action])

  if (!isOpen) return null

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">{isCreateMode ? 'Add New Room Type' : 'Edit Room Type'}</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors disabled:opacity-50"
              disabled={isLoading}
            >
              <FaTimes className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Tier Type */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Tier Type
              </label>
              {isEditing ? (
                <select
                  value={selectedTierType || ""}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    const selectedOption = tierType?.find(tier => tier.id === selectedId);
                    setSelectedTierType(selectedId);
                    handleInputChange('tierTypeId', selectedId); // Store ID for backend
                    handleInputChange('tierType', selectedOption?.name || selectedOption?.tierType || ''); // Store name for display
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select Tier Type</option>
                  {tierType && tierType.length > 0 ? (
                    tierType.map((tier, index) => (
                      <option key={index} value={tier.id}>
                        {tier.name || tier.tierType}
                      </option>
                    ))
                  ) : (
                    <option disabled>No tiers available</option>
                  )}
                </select>
              ) : (
                <div className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
                  {formData.tierType || 'Not specified'}
                </div>
              )}
            </div>

            {/* Room Type */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Room Type
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.roomType}
                  onChange={(e) => handleInputChange('roomType', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter room type"
                />
              ) : (
                <div className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
                  {formData.roomType || 'Not specified'}
                </div>
              )}
            </div>

            {/* Price */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Price
              </label>
              {isEditing ? (
                <input
                  type="number"
                  value={formData.price}
                  onChange={(e) => handleInputChange('price', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter price"
                  min="0"
                  step="0.01"
                />
              ) : (
                <div className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
                  {formData.price ? `$${formData.price}` : 'Not specified'}
                </div>
              )}
            </div>

            {/* Room Code */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Room Code
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.roomCode}
                  onChange={(e) => handleInputChange('roomCode', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter room code"
                />
              ) : (
                <div className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
                  {formData.roomCode || 'Not specified'}
                </div>
              )}
            </div>
          </div>

          {/* Description Section - Full Width */}
          <div className="mt-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Description
              </label>
              {isEditing ? (
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter room description"
                  rows={4}
                />
              ) : (
                <div className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 min-h-[100px]">
                  {formData.description || 'No description provided'}
                </div>
              )}
            </div>
          </div>

          {/* Images Section */}
          <div className="mt-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Images
            </label>
            
            {isEditing && (
              <div className="mb-4">
                <label className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-indigo-500 transition-colors">
                  <div className="flex flex-col items-center">
                    <FaImage className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-500">Click to add images</span>
                  </div>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageAdd}
                    className="hidden"
                  />
                </label>
              </div>
            )}

            {/* Images Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {formData.images.map((image, index) => (
                <div key={image.id || index} className="relative group">
                  <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                    <img
                      src={image.preview || image.url || image}
                      alt={`Room image ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {isEditing && (
                    <button
                      onClick={() => handleImageRemove(image.id || index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <FaTimes className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
              
              {formData.images.length === 0 && !isEditing && (
                <div className="col-span-full flex items-center justify-center h-32 border-2 border-dashed border-gray-300 rounded-lg">
                  <span className="text-gray-500">No images available</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-between items-center border-t">
          <div className="flex space-x-3">
            {!isEditing && isUpdateMode ? (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                disabled={isLoading}
              >
                <FaEdit className="w-4 h-4 mr-2" />
                Edit
              </button>
            ) : isEditing ? (
              <button
                onClick={() => {
                    if(isCreateMode){
                        onClose()
                    }else{
                        setIsEditing(false)
                    }
                }}
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
                disabled={isLoading}
              >
                <FaTimes className="w-4 h-4 mr-2" />
                Cancel
              </button>
            ) : null}
          </div>
          
          <div className="flex space-x-3">
            {isEditing ? (
              <button
                onClick={handleUpdate}
                className="flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <FaEdit className="w-4 h-4 mr-2" />
                )}
                {isCreateMode ? 'Create' : 'Update'}
              </button>
            ) : isUpdateMode ? (
              <button
                onClick={handleDeleteClick}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <FaTrash className="w-4 h-4 mr-2" />
                )}
                Delete
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {/* Notification */}
      <Notification
        show={showNotification}
        message={notificationMessage}
        type={notificationType}
        onClose={hideNotification}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        show={showConfirm}
        title={confirmTitle}
        message={confirmMessage}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        isLoading={confirmLoading}
      />
    </div>
    </>
  )
}

export default RoomType
